export class RedirectError extends Error {
  constructor(
    public readonly pathname: string,
    public readonly cause: string,
  ) {
    super(cause)
  }
}

export class UnauthorizedError extends Error {
  constructor(
    message: string,
    public readonly cause: string,
  ) {
    super(message)
  }
}

export const redirectError = (pathname: string, cause = 'REDIRECT') => {
  throw new RedirectError(pathname, cause)
}

export const isRedirectError = (error: unknown): error is RedirectError => {
  return error instanceof RedirectError
}

export const unauthorizedError = (message: string, cause = 'UNAUTHORIZED') => {
  throw new UnauthorizedError(message, cause)
}

export const isUnauthorizedError = (error: unknown): error is UnauthorizedError => {
  return error instanceof UnauthorizedError
}

export const isTooManyRequestError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false
  }
  if (typeof error.cause !== 'object' || error.cause === null) {
    return false
  }
  return 'resultCd' in error.cause && Number(error.cause.resultCd) === 429
}

export const DAILY_LESSON_LIMIT_ERROR_CODE = 'INVALID_DAILY_LESSON_LIMIT'
export const DAILY_LESSON_LIMIT_REASON = 'daily_lesson_limit'
export const TUTOR_NOT_FOUND_ERROR_CODE = 'TUTOR_NOT_FOUND'
export const ACTIVE_RESERVATION_LIMIT_ERROR_CODE = 'INVALID_DOUBLE_PACK_DUPLICATE_BOOK'

type ErrorPayload = Record<string, unknown>

const toErrorPayload = (value: unknown): ErrorPayload | null =>
  typeof value === 'object' && value !== null ? (value as ErrorPayload) : null

const readString = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null)

export const isActiveReservationLimitError = (value: unknown): boolean => {
  const root = toErrorPayload(value)
  const cause = toErrorPayload(root?.cause)
  const rawData = toErrorPayload(cause?.rawData ?? cause?.raw_data ?? cause?.data)
  return [root, cause, rawData].some(
    (payload) =>
      payload &&
      [payload.code, payload.resultCdName, payload.result_cd_name].some(
        (code) => code === ACTIVE_RESERVATION_LIMIT_ERROR_CODE,
      ),
  )
}

const TUTOR_NOT_FOUND_MESSAGE_PATTERN = /튜터 정보를 찾을 수 없습니다/

/**
 * 예약 API마다 오류를 일반 응답 또는 podoFetcher HttpError로 전달하므로
 * 코드와 기존 한국어 메시지를 모두 확인해 동일한 다국어 문구로 치환한다.
 */
export const isTutorNotFoundError = (value: unknown): boolean => {
  const root = toErrorPayload(value)
  if (!root) {
    return false
  }

  const cause = toErrorPayload(root.cause)
  const rawData = toErrorPayload(cause?.rawData ?? cause?.raw_data ?? cause?.data)
  const payloads = [root, cause, rawData].filter((payload): payload is ErrorPayload => payload !== null)

  return payloads.some((payload) => {
    const hasErrorCode = [payload.code, payload.resultCdName, payload.result_cd_name].some(
      (code) => readString(code) === TUTOR_NOT_FOUND_ERROR_CODE,
    )
    const hasLegacyMessage = [payload.message, payload.resultMsg, payload.result_msg, payload.result]
      .map(readString)
      .some((message) => message !== null && TUTOR_NOT_FOUND_MESSAGE_PATTERN.test(message))

    return hasErrorCode || hasLegacyMessage
  })
}

/**
 * 일반 예약 응답과 podoFetcher HttpError 양쪽에서 1일 1회 정책 오류를 같은 형태로 읽는다.
 * podoFetcher 는 실제 서버 메시지를 error.cause 에 보존하고 error.message 는
 * PODO_API_REQUEST_FAILED 로 덮기 때문에 cause 를 우선한다.
 */
export const parseDailyLessonLimitError = (value: unknown): { message: string | null } | null => {
  const root = toErrorPayload(value)
  if (!root) {
    return null
  }

  const cause = toErrorPayload(root.cause)
  const rawData = toErrorPayload(cause?.rawData ?? cause?.raw_data)
  const payloads = [cause, root, rawData].filter((payload): payload is ErrorPayload => payload !== null)

  const isDailyLessonLimitError = payloads.some((payload) =>
    [payload.code, payload.resultCdName, payload.result_cd_name].some(
      (code) => readString(code) === DAILY_LESSON_LIMIT_ERROR_CODE,
    ),
  )

  if (!isDailyLessonLimitError) {
    return null
  }

  const message = payloads
    .flatMap((payload) => [payload.message, payload.resultMsg, payload.result_msg])
    .map(readString)
    .find((candidate) => candidate !== null && candidate !== 'PODO_API_REQUEST_FAILED')

  return { message: message ?? null }
}
